import {
  IconArrowLeft,
  IconDots,
  IconDownload,
  IconLink,
  IconLoader2,
  IconShare2,
  IconTrash,
} from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Loader from "../../components/Loader";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { DesktopBreadcrumb } from "../../components/app/desktopTopNav/DesktopBreadcrumb";
import { DesktopTopNav } from "../../components/app/desktopTopNav/DesktopTopNav";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { displayToast } from "../../components/app/AppToast";
import { DesktopConfirmDialog } from "../../components/app/DesktopConfirmDialog";
import {
  DesktopMenu,
  DesktopMenuDivider,
  DesktopMenuItem,
} from "../../components/ui/DesktopMenu";
import { usePopoverAnchor } from "../../components/ui/Popover";
import { Logo } from "../../components/ui/Logo";
import { Tooltip } from "../../components/ui/Tooltip";
import { authorLine } from "../../domains/article/utils/authorLine";
import { renderDate } from "../../domains/app/utils/renderDate";
import { articleDetailQuery } from "../article/hooks/useArticleData";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { Button } from "../../components/ui/Button";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import {
  gaEventVisualAbstractRendered,
  gaEventVisualAbstractUsefulnessAnswered,
} from "../../tools/analytics/domains/article.analytics";
import {
  apiClient,
  clearApiQuery,
  revalidateApiQuery,
  useApiQuery,
  type Api,
} from "../../services/api";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { VisualAbstract } from "./VisualAbstract";
import { VisualAbstractArticleRecap } from "./VisualAbstractArticleRecap";
import { VisualAbstractBlockerNotice } from "./VisualAbstractBlockerNotice";
import {
  useShareVisualAbstract,
  type UseShareVisualAbstract,
} from "./useShareVisualAbstract";

type ReadyArtifact = Extract<
  Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"],
  { status: "ready" }
>;

export default function VisualAbstractPreviewPage({
  id,
}: {
  id: string;
}): React.ReactElement {
  const query = useApiQuery("/webapp/private/visual-abstracts/:id", {
    param: { id },
    staleTime: 0,
  });

  if (query.state === "pending") return <Loader expanded />;
  if (query.state === "error") {
    if (query.status === 404) return <VisualAbstractPreviewUnavailable />;
    throw query.error;
  }

  switch (query.body.status) {
    case "queued":
    case "generating":
      return <Loader expanded />;
    case "failed":
      return <VisualAbstractPreviewUnavailable />;
    case "ready":
      return <ReadyVisualAbstractPreview artifact={query.body} />;
    default:
      throw new Error(query.body satisfies never);
  }
}

function ReadyVisualAbstractPreview({
  artifact,
}: {
  artifact: ReadyArtifact;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { triggerShare, renderShareSheet, desktop } =
    useShareVisualAbstract(artifact);

  const dimensions = {
    visualAbstractId: artifact.id,
    roomId: artifact.roomId,
    language: artifact.language,
    family: artifact.family,
    sourceType: artifact.sourceType,
    generatorVersion: artifact.generatorVersion,
  };

  // The owner's poster actually painting — the step between a generation that
  // succeeded and a share. Keyed on the artifact so reopening a different one
  // in the same session counts again.
  useEffectOnce(() => {
    gaEventVisualAbstractRendered({
      ...dimensions,
      blockers: artifact.blockers,
    });
  }, [artifact.id]);

  const poster = (
    <VisualAbstract
      actions={{
        kind: "preview",
        onUsefulness: (useful) => {
          gaEventVisualAbstractUsefulnessAnswered({
            ...dimensions,
            surface: "preview",
            useful,
          });
        },
      }}
      view={artifact.view}
    />
  );

  if (isDesktopShell())
    return (
      <DesktopPreview artifact={artifact} share={desktop} poster={poster} />
    );

  return (
    <ScrollableLayout
      background="var(--color-ds-cream)"
      header={
        <ContentHeader
          actions={
            <button
              aria-label={t("Share")}
              className="flex cursor-pointer items-center border-0 bg-transparent p-0"
              onClick={triggerShare}
              type="button"
            >
              <IconShare2 />
            </button>
          }
          onBack={() => {
            history.goBack();
          }}
          title={t("Preview")}
          transparent
        />
      }
      headerClassName="bg-white/90 backdrop-blur-md"
    >
      {/* Desktop counterpart of ContentHeader, which is md:hidden. The white
          band is full-bleed so it reads as a header over the cream page, the
          way the mobile header does; only the button row is width-capped. */}
      <div className="hidden bg-ds-white md:block">
        <div className="mx-auto flex w-full max-w-6xl justify-between p-3 ">
          <Button
            leftIcon={<IconArrowLeft />}
            onClick={() => {
              history.goBack();
            }}
            size="sm"
            variant="ghost"
          >
            {t("Back")}
          </Button>
          <Button
            leftIcon={<IconShare2 />}
            onClick={triggerShare}
            size="sm"
            variant="ghost"
          >
            {t("Share")}
          </Button>
        </div>
      </div>
      {poster}
      {renderShareSheet()}
    </ScrollableLayout>
  );
}

/**
 * The desktop viewer (Figma "D2.va-viewer"): the poster at its export width in
 * a centred column, so the screen shows the image a download produces, and a
 * panel beside it for the source, the attribution and the two ways it leaves
 * Juisci.
 */
function DesktopPreview({
  artifact,
  share,
  poster,
}: {
  artifact: ReadyArtifact;
  share: UseShareVisualAbstract["desktop"];
  poster: React.ReactElement;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  return (
    // From `xl` the panel stands right of the poster. Below the 1280px window
    // it would leave the poster a strip, so the page becomes one scrolling
    // column: the poster, then the panel under it.
    <div className="flex h-full flex-col overflow-y-auto bg-ds-cream xl:flex-row xl:overflow-hidden">
      <div className="flex shrink-0 flex-col xl:min-w-0 xl:flex-1 xl:shrink">
        <DesktopTopNav
          context={
            <DesktopBreadcrumb
              // The previous page may not be the library (a poster opened
              // from a conversation), so Back keeps its own chevron.
              back={{
                kind: "separate",
                onBack: () => {
                  history.goBack();
                },
              }}
              trail={[
                { label: t("Visual Abstracts"), to: "/visual-abstracts" },
              ]}
              current={artifact.view.header.shortTitle}
            />
          }
          trailing={{
            kind: "actions",
            content: <VisualAbstractMenu artifact={artifact} share={share} />,
          }}
          // The page's own colour shows through the bar, as in the design.
          surface="default"
          className="bg-transparent"
        />
        {/* Scrolls both ways: a desktop window narrowed below the poster, the
            panel and the sidebar keeps the whole poster reachable. */}
        <main className="px-6 pt-8 pb-12 max-xl:overflow-x-auto xl:min-h-0 xl:flex-1 xl:overflow-auto">
          {/* The export width (see `useShareVisualAbstract`), so the poster on
              screen is the single-column one the downloaded image carries. */}
          <div className="mx-auto w-120 overflow-hidden rounded-2xl border border-solid border-ds-slate-100 shadow-md">
            {poster}
          </div>
        </main>
      </div>
      <aside className="flex shrink-0 flex-col px-6 pb-12 xl:w-gpt-context-panel xl:py-4 xl:pr-6 xl:pl-0">
        <section className="mx-auto flex w-120 flex-col gap-5 rounded-2xl bg-ds-white px-6 pt-5 pb-6 shadow-xs xl:mx-0 xl:min-h-0 xl:w-auto xl:flex-1 xl:overflow-y-auto">
          <div className="flex flex-col gap-5">
            <span className="font-dm-sans text-footnote/4.5 font-semibold text-ds-slate-500">
              {t("Source")}
            </span>
            <PreviewArticleRecap artifact={artifact} />
            <div className="flex flex-col gap-2">
              <span className="font-dm-sans text-footnote/4.5 font-semibold text-ds-slate-500">
                {t("Generated by")}
              </span>
              <div className="flex items-center gap-2.5">
                <PanelBrandMark view={artifact.view} />
                <span className="font-dm-sans text-footnote/4.5 text-ds-slate-700">
                  {renderDate(artifact.createdAt)}
                </span>
              </div>
            </div>
            {share.isPublishable ? null : (
              <VisualAbstractBlockerNotice blockers={artifact.blockers} />
            )}
          </div>
          <div className="mt-auto flex flex-col gap-2.5">
            <BlockedShareTooltip isPublishable={share.isPublishable}>
              <Button
                variant="primary"
                size="lg"
                type="button"
                className={
                  share.isPublishable ? "w-full" : "pointer-events-none w-full"
                }
                leftIcon={
                  share.state.kind === "sharing" &&
                  share.state.channel === "image" ? (
                    <IconLoader2 className="animate-spin" />
                  ) : (
                    <IconDownload />
                  )
                }
                disabled={
                  !share.isPublishable || share.state.kind === "sharing"
                }
                onClick={() => {
                  void share.downloadImage();
                }}
              >
                {t("Download")}
              </Button>
            </BlockedShareTooltip>
            <BlockedShareTooltip isPublishable={share.isPublishable}>
              <Button
                variant="outline"
                size="lg"
                type="button"
                className={
                  share.isPublishable ? "w-full" : "pointer-events-none w-full"
                }
                leftIcon={
                  share.state.kind === "sharing" &&
                  share.state.channel === "public-link" ? (
                    <IconLoader2 className="animate-spin" />
                  ) : (
                    <IconLink />
                  )
                }
                disabled={
                  !share.isPublishable || share.state.kind === "sharing"
                }
                onClick={() => {
                  void share.copyLink();
                }}
              >
                {t("Copy link")}
              </Button>
            </BlockedShareTooltip>
          </div>
        </section>
      </aside>
      {share.renderExportSurface()}
    </div>
  );
}

/**
 * The owner's actions on the poster behind the top bar's 3-dots trigger
 * (Figma "D2.va-viewer.menu"): Download and Copy link, as in the panel, then
 * Delete. A blocked poster greys out Download and Copy link, as the panel does.
 */
function VisualAbstractMenu({
  artifact,
  share,
}: {
  artifact: ReadyArtifact;
  share: UseShareVisualAbstract["desktop"];
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const [view, setView] = useState<"closed" | "menu" | "delete">("closed");
  const anchor = usePopoverAnchor({
    isOpen: view === "menu",
    onDismiss: () => {
      setView("closed");
    },
    edge: "right",
  });
  const action = useSingleFlightAction();
  const isRunning = action.state.status === "running";

  function confirmDelete(): void {
    void action.run(async () => {
      try {
        await apiClient.delete("/webapp/private/visual-abstracts/:id", {
          param: { id: artifact.id },
        });
      } catch {
        // The api client's onRequestError already captured the cause.
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      history.replace("/visual-abstracts");
      clearApiQuery("/webapp/private/visual-abstracts/:id", {
        param: { id: artifact.id },
      });
      // The library's card and the article's pill both list this poster. The
      // artifact does not name its article, so every article's list is
      // refreshed: only the mounted ones refetch.
      await Promise.all([
        revalidateApiQuery("/webapp/private/visual-abstracts/library"),
        revalidateApiQuery("/webapp/private/articles/:id/visual-abstracts"),
      ]);
      displayToast(t("Visual abstract deleted"), "success");
    });
  }

  return (
    <>
      <button
        ref={anchor.triggerRef}
        type="button"
        aria-label={t("More")}
        aria-haspopup="menu"
        aria-expanded={view === "menu"}
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent p-0 text-ds-slate-950 hover:bg-ds-black/6"
        onClick={() => {
          setView(view === "menu" ? "closed" : "menu");
        }}
      >
        <IconDots className="size-4.5" stroke={1.5} />
      </button>
      {view === "menu" ? (
        <DesktopMenu anchor={anchor} className="w-65.5">
          <DesktopMenuItem
            description={null}
            icon={IconDownload}
            label={t("Download")}
            trailing={null}
            tone="default"
            action={
              share.isPublishable
                ? {
                    kind: "button",
                    onClick: () => {
                      setView("closed");
                      void share.downloadImage();
                    },
                  }
                : { kind: "disabled" }
            }
          />
          <DesktopMenuItem
            description={null}
            icon={IconLink}
            label={t("Copy link")}
            trailing={null}
            tone="default"
            action={
              share.isPublishable
                ? {
                    kind: "button",
                    onClick: () => {
                      setView("closed");
                      void share.copyLink();
                    },
                  }
                : { kind: "disabled" }
            }
          />
          <DesktopMenuDivider />
          <DesktopMenuItem
            description={null}
            icon={IconTrash}
            label={t("Delete")}
            trailing={null}
            tone="danger"
            action={{
              kind: "button",
              onClick: () => {
                setView("delete");
              },
            }}
          />
        </DesktopMenu>
      ) : null}
      {view === "delete" ? (
        <DesktopConfirmDialog
          title={t("Delete visual abstract?")}
          confirmLabel={t("Delete")}
          note={t("This can't be undone.")}
          disabled={isRunning}
          confirmDisabled={false}
          onConfirm={confirmDelete}
          onCancel={() => {
            if (!isRunning) setView("closed");
          }}
        >
          <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-700">
            {t(
              "This visual abstract will be permanently deleted. Its share link, if any, will stop working."
            )}
          </p>
        </DesktopConfirmDialog>
      ) : null}
    </>
  );
}

/**
 * Why a blocked poster's actions are greyed out, on hover and on keyboard
 * focus. The disabled button lets the pointer through (`pointer-events-none`
 * at the call site), so the tooltip's own trigger receives it: browsers do not
 * reliably report the pointer over a disabled control.
 */
function BlockedShareTooltip({
  isPublishable,
  children,
}: {
  isPublishable: boolean;
  children: React.ReactElement;
}): React.ReactElement {
  const { t } = useTranslation();
  if (isPublishable) return children;
  return (
    <Tooltip
      trigger={{ kind: "inline", content: children }}
      side="below"
      content={{
        kind: "card",
        title: t("Not shareable yet"),
        body: t(
          "This visual abstract can be downloaded or shared once the checks above are complete."
        ),
      }}
    />
  );
}

/**
 * The paper the poster summarises, from the article page's own query: the
 * poster's view carries its short title and citation, not the article's full
 * title, access or authors.
 */
function PreviewArticleRecap({
  artifact,
}: {
  artifact: ReadyArtifact;
}): React.ReactElement {
  const { t } = useTranslation();
  const query = useApiQuery("/webapp/public/articles/:slug", {
    param: { slug: artifact.view.articleSlug },
    query: articleDetailQuery(artifact.roomId),
  });
  switch (query.state) {
    case "pending":
      return (
        <div className="flex justify-center py-6">
          <IconLoader2 className="size-6 animate-spin text-ds-slate-400" />
        </div>
      );
    case "error":
      return (
        <p className="m-0 font-dm-sans text-ui-note text-ds-slate-500">
          {t("This content is no longer available.")}
        </p>
      );
    case "success":
      return (
        <VisualAbstractArticleRecap
          article={query.body}
          roomId={artifact.roomId}
          authors={authorLine(query.body.primary_author, query.body.authors)}
        />
      );
    default:
      throw new Error(query satisfies never);
  }
}

/** The poster's own mark, at the panel's scale and on its white surface. */
function PanelBrandMark({
  view,
}: {
  view: ReadyArtifact["view"];
}): React.ReactElement {
  switch (view.branding.source) {
    case "juisci":
      return (
        <Logo
          className="h-9"
          tone="color"
          variant={view.language === "fr" ? "tagline-fr" : "tagline-en"}
        />
      );
    case "room": {
      const logoAssetId = view.branding.logoAssetId;
      const asset = view.assets.find(
        (candidate) => candidate.id === logoAssetId
      );
      if (asset === undefined)
        throw new Error(
          `Visual abstract branding logo asset ${logoAssetId} is missing`
        );
      return (
        <img
          alt={view.branding.organisationName}
          className="max-h-9 max-w-28 object-contain"
          src={asset.url}
        />
      );
    }
    default:
      throw new Error(view.branding satisfies never);
  }
}

function VisualAbstractPreviewUnavailable(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <main className="grid min-h-dvh place-items-center bg-ds-cream p-6 text-center text-ds-mint-950">
      <div>
        <h1 className="mb-2 mt-0 text-heading-lg font-bold">
          {t("Visual abstracts")}
        </h1>
        <p className="m-0 text-footnote text-ds-slate-600">
          {t("This content is no longer available.")}
        </p>
      </div>
    </main>
  );
}
